import type { ReactNode } from 'react'
import { Avatar } from '../../common/Avatar'
import { formatDateTime } from '../../../utils/date-format'
import type { ProcessSubjectMessage } from '../../../types/occurrences'

/**
 * Moldura das mensagens da conversa: avatar à esquerda e card branco com a
 * linha "**Nome** … · data" no topo. Usada pela abertura da ocorrência e
 * pelas mensagens de usuário. `own`: mensagem de quem está olhando — à
 * direita, em azul-claro e sem avatar, como nos aplicativos de conversa.
 */
export function MessageCard({
  messageId,
  authorName,
  picture,
  header,
  highlighted = false,
  own = false,
  actions,
  children,
}: {
  messageId: number
  authorName: string
  picture?: string | null
  /** Texto depois do nome ("abriu a ocorrência · 08/09, 17:00"). */
  header: ReactNode
  /** Destaque de quem foi mencionado. */
  highlighted?: boolean
  /** Mensagem de quem está olhando. */
  own?: boolean
  /** Botões que aparecem ao passar o mouse (responder, apagar). */
  actions?: ReactNode
  children?: ReactNode
}) {
  return (
    <div
      data-message-id={messageId}
      className={`group flex items-start gap-3.5 rounded-xl ${own ? 'justify-end' : ''}`}
    >
      {!own && <Avatar name={authorName} picture={picture} size={40} variant="soft" />}
      <div
        // Até 80% da conversa: em área estreita (ocorrência, com o painel ao
        // lado) a diferença esquerda × direita continua clara.
        className={`relative min-w-0 max-w-[min(57.5rem,80%)] flex-1 rounded-xl border px-5 pb-3.5 pt-3 transition-shadow ${
          own
            ? 'border-blue-100 bg-blue-50 dark:border-blue-900/60 dark:bg-blue-900/25'
            : highlighted
              ? 'border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-900/15'
              : 'border-border-subtle bg-surface'
        }`}
      >
        <div className="text-[15px] text-muted">
          <strong className="font-semibold text-body">{authorName}</strong> {header}
        </div>
        {children}
        {actions && (
          <div className="absolute right-3 top-3 flex items-center gap-1 opacity-0 transition group-hover:opacity-100">
            {actions}
          </div>
        )}
      </div>
    </div>
  )
}

/** Bloco de citação ("Citado de Fulano · cargo · data" + o texto). */
export function QuoteBlock({
  message,
  onJump,
}: {
  message: ProcessSubjectMessage
  onJump?: () => void
}) {
  const name = message.author?.full_name || 'Usuário'
  const text = message.deleted_at
    ? 'Mensagem apagada'
    : (message.content ?? '').trim() ||
      (message.attachments?.[0] ? `📎 ${message.attachments[0].name}` : '(sem conteúdo)')
  return (
    <button
      type="button"
      onClick={onJump}
      title="Ir para a mensagem citada"
      className="mt-3 block w-full rounded-lg bg-surface-muted px-4 py-3 text-left transition hover:bg-surface-raised"
    >
      <span className="flex items-center gap-2 text-[13px] text-muted">
        <Avatar name={name} size={22} variant="soft" />
        Citado de <strong className="font-semibold text-body">{name}</strong>
        {message.author?.role && <span>· {message.author.role}</span>}
        <span>· {formatDateTime(message.created_at)}</span>
      </span>
      <span className="mt-2 line-clamp-4 block whitespace-pre-wrap text-sm text-body">{text}</span>
    </button>
  )
}
